<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import { createStaffBriefing, getProject, listStaffBriefings } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["staff-briefings", selectedProjectId],
  queryFn: () => listStaffBriefings(selectedProjectId.value),
});

const { data: project } = useQuery({
  queryKey: ["project", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? getProject(selectedProjectId.value)
      : Promise.reject(new Error("No project")),
  enabled: () => !!selectedProjectId.value,
});

const open = (id: string): void => {
  void router.push({
    name: "briefing-detail",
    params: { projectId: selectedProjectId.value, id },
  });
};

// Standalone / ad-hoc briefing (toolbox talk, visitor brief). The primary
// flow is from a work pack or a site diary, where it's pre-populated + linked.
const today = new Date().toISOString().slice(0, 10);
const showAdhoc = ref(false);
const form = reactive({ briefing_date: today, briefer_name: "", task_description: "" });
const creating = ref(false);
const createError = ref<string | null>(null);
const projectName = computed(() => project.value?.name ?? "");

async function create(): Promise<void> {
  if (!selectedProjectId.value || !projectName.value) {
    createError.value = "No project selected.";
    return;
  }
  if (!form.briefer_name.trim()) {
    createError.value = "Briefer name is required.";
    return;
  }
  creating.value = true;
  createError.value = null;
  try {
    const id = await createStaffBriefing({
      project_id: selectedProjectId.value,
      project_name: projectName.value,
      project_code: project.value?.code ?? undefined,
      briefing_date: form.briefing_date,
      briefer_name: form.briefer_name,
      task_description: form.task_description || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["staff-briefings"] });
    open(id);
  } catch (e) {
    createError.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <h1 class="text-xl font-semibold mb-1">
      Briefings & sign-on registers
    </h1>
    <p class="mb-4 max-w-2xl text-sm text-neutral-500">
      Briefings are normally started from a <strong>work pack</strong> ("Start
      site briefing") or a <strong>site diary</strong> ("Start sign-on
      briefing") — pre-filled and linked to the shift — then signed on the
      mobile app. Started ones appear here as the sign-on register. For a
      standalone toolbox talk or visitor brief, start one below.
    </p>

    <!-- Ad-hoc / standalone briefing -->
    <section class="mb-6 rounded-xl border border-neutral-200 bg-neutral-0 p-5">
      <div class="flex items-center justify-between gap-3">
        <h2 class="font-semibold">
          Standalone briefing
        </h2>
        <button
          type="button"
          class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          @click="showAdhoc = !showAdhoc"
        >
          {{ showAdhoc ? "Cancel" : "+ New briefing" }}
        </button>
      </div>
      <div
        v-if="showAdhoc"
        class="mt-4 grid max-w-xl gap-x-4 gap-y-3 sm:grid-cols-2"
      >
        <p class="text-sm text-neutral-500 sm:col-span-2">
          Filed against
          <span class="font-medium text-neutral-700">{{ projectName || "this project" }}</span>.
          Not tied to a work pack or diary.
        </p>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Date</span>
          <input
            v-model="form.briefing_date"
            type="date"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Briefer name</span>
          <input
            v-model="form.briefer_name"
            type="text"
            placeholder="Who is giving the brief"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <label class="grid gap-1 text-sm sm:col-span-2">
          <span class="text-neutral-500">Topic / task <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.task_description"
            type="text"
            placeholder="e.g. Toolbox talk — manual handling"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <p
          v-if="createError"
          class="text-sm text-semantic-danger sm:col-span-2"
        >
          {{ createError }}
        </p>
        <div class="sm:col-span-2">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="creating || !projectName || !form.briefer_name.trim()"
            @click="create"
          >
            {{ creating ? "Starting…" : "Start briefing" }}
          </button>
        </div>
      </div>
    </section>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="rounded-xl border border-dashed border-neutral-300 p-6 text-sm text-neutral-500"
    >
      No briefings yet. Start one from a work pack or a site diary (it'll be
      pre-filled and linked to the shift), or use "+ New briefing" above for a
      standalone one.
    </div>
    <div
      v-else-if="data"
      class="rounded-xl bg-neutral-0 shadow overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead class="bg-neutral-50 text-neutral-500 text-left">
          <tr>
            <th class="px-4 py-2 font-medium">
              Date
            </th>
            <th class="px-4 py-2 font-medium">
              Project
            </th>
            <th class="px-4 py-2 font-medium">
              Rail week
            </th>
            <th class="px-4 py-2 font-medium">
              Delivered
            </th>
            <th class="px-4 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="b in data"
            :key="b.id"
            class="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
            @click="open(b.id)"
          >
            <td class="px-4 py-2">
              {{ b.briefing_date }}
            </td>
            <td class="px-4 py-2">
              {{ b.project_name }}
            </td>
            <td class="px-4 py-2">
              {{ b.rail_week ?? "—" }}
            </td>
            <td class="px-4 py-2">
              {{ b.delivered ? "Yes" : "No" }}
            </td>
            <td class="px-4 py-2">
              {{ b.status }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageLayout>
</template>
